import React, { useState } from 'react';
import { FileJson, Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from '@roger/ui-components';
import EOEPreviewPopup from '../EOEPreviewPopup';
import {
  client,
  getDraftBillingCycles,
  setBillingCycleStatusPendingApproval,
} from '../../../services/api';
import type { GetBillingCyclesResponse } from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { useBillingCycles } from '../../../hooks/useBillingCycles';

interface EOEBillingReadyEntryProps {
  entry: GetBillingCyclesResponse;
  index?: number;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onPreview: (entry: GetBillingCyclesResponse) => void;
}

interface EOEReadyTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEBillingReadyEntry: React.FC<EOEBillingReadyEntryProps> = ({
  entry,
  index,
  isSelected,
  onToggle,
  onPreview,
}) => {
  const startDate = formatDateToMMDDYYYY(entry.start_date);
  const endDate = formatDateToMMDDYYYY(entry.end_date);
  const isAlternate = index !== undefined && index % 2 === 1;

  return (
    <TableRow
      className={
        isAlternate
          ? 'bg-muted hover:bg-muted'
          : 'bg-background hover:bg-background'
      }
    >
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(entry.billing_cycle_id)}
        />
      </TableCell>
      <TableCell>{entry.patient_name}</TableCell>
      <TableCell>{entry.patient_mrn}</TableCell>
      <TableCell>{entry.clinic_name}</TableCell>
      <TableCell>{entry.insurance_name}</TableCell>
      <TableCell>
        <span>{startDate} -</span>
        <br />
        <span>{endDate}</span>
      </TableCell>
      <TableCell>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={() => onPreview(entry)}
          className='text-muted-foreground hover:text-foreground'
          aria-label='Open EOE draft'
        >
          <FileJson className='h-4 w-4' />
        </Button>
      </TableCell>
    </TableRow>
  );
};

const EOEReadyTab: React.FC<EOEReadyTabProps> = ({ onCountsInvalidated }) => {
  const [draftPopupCycle, setDraftPopupCycle] =
    useState<GetBillingCyclesResponse | null>(null);

  const {
    data,
    isLoading,
    isSubmitting,
    selectedIds,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
  } = useBillingCycles<GetBillingCyclesResponse, { ready_for_review: boolean }>(
    {
      fetchFn: getDraftBillingCycles,
      fetchPayload: { ready_for_review: true },
      errorMessage: 'Could not fetch data for the "Ready" tab.',
      getId: item => item.billing_cycle_id,
      getName: item => item.patient_name,
    }
  );

  const handleCreate = () => {
    void mutateSelected(
      id =>
        setBillingCycleStatusPendingApproval({
          client,
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles failed to update',
      'The following billing cycles could not be set to Pending Approval',
      onCountsInvalidated
    );
  };

  return (
    <div className='flex flex-col gap-4'>
      {isLoading ? (
        <p className='text-muted-foreground text-center py-10'>Loading...</p>
      ) : data.length === 0 ? (
        <p className='text-muted-foreground text-center py-10'>
          There are currently no records to display
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Checkbox
                  checked={data.length > 0 && selectedIds.size === data.length}
                  onCheckedChange={handleToggleAll}
                />
              </TableHead>
              <TableHead>Patient Name</TableHead>
              <TableHead>MRN</TableHead>
              <TableHead>Clinic</TableHead>
              <TableHead>Insurance</TableHead>
              <TableHead>Billing Period</TableHead>
              <TableHead>Claim Preview</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((entry, index) => (
              <EOEBillingReadyEntry
                key={entry.billing_cycle_id}
                entry={entry}
                index={index}
                isSelected={selectedIds.has(entry.billing_cycle_id)}
                onToggle={handleToggleId}
                onPreview={setDraftPopupCycle}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <div className='flex justify-end'>
        <Button
          variant='purple'
          disabled={selectedIds.size === 0 || isSubmitting || isLoading}
          onClick={handleCreate}
        >
          {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Create Claim
        </Button>
      </div>

      <EOEPreviewPopup
        billingCycle={draftPopupCycle}
        onOpenChange={open => !open && setDraftPopupCycle(null)}
      />
    </div>
  );
};

export default EOEReadyTab;
